import { generateKeyPair, publicKeyFromPrivateKey } from './crypto';
import { generateUuid } from './bip32';
import { validateMnemonic, mnemonicToSeedSync } from '@scure/bip39';
import { wordlist as englishWordlist } from '@scure/bip39/wordlists/english.js';
import { HDKey } from '@scure/bip32';
import { bytesToHex } from '@noble/hashes/utils';
import {
  encryptPrivateKey,
  decryptPrivateKey,
  type EncryptedPayload,
} from './keyProtection';
import { setUnlockedPrivateKey } from './sessionUnlockedKey';
import type { LocalIdentity } from '../types/identity';

const BIP32_DERIVATION_PATH = "m/44'/0'/0'/0/0";

const STORAGE_KEY_IDENTITY = 'userwallet_identity';
const STORAGE_KEY_IDENTITY_ENCRYPTED = 'userwallet_identity_encrypted';

/**
 * Get stored local identity.
 */
export function getStoredIdentity(): LocalIdentity | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_IDENTITY);
    if (stored === null) {
      return null;
    }
    return JSON.parse(stored) as LocalIdentity;
  } catch (error) {
    console.error('Error reading stored identity:', error);
    return null;
  }
}

/**
 * Store local identity.
 */
export function storeIdentity(identity: LocalIdentity): void {
  localStorage.setItem(STORAGE_KEY_IDENTITY, JSON.stringify(identity));
}

/**
 * Create a new local identity.
 */
export function createIdentity(name?: string): LocalIdentity {
  const keyPair = generateKeyPair();
  const identity: LocalIdentity = {
    uuid: generateUuid(),
    privateKey: keyPair.privateKey,
    publicKey: keyPair.publicKey,
    name,
    t0_anniversaire: Date.now(),
    version: '1.0',
  };
  storeIdentity(identity);
  return identity;
}

/**
 * Derive secp256k1 private key (hex) from BIP39 mnemonic.
 * Uses path m/44'/0'/0'/0/0. Passphrase is not supported (empty string).
 */
function privateKeyFromMnemonic(mnemonic: string): string {
  const seed = mnemonicToSeedSync(mnemonic, '');
  const hdkey = HDKey.fromMasterSeed(seed);
  const derived = hdkey.derive(BIP32_DERIVATION_PATH);
  const pk = derived.privateKey;
  if (pk === null) {
    throw new Error('BIP32 derivation did not yield a private key');
  }
  return bytesToHex(pk);
}

/**
 * Import identity from either:
 * - Raw hex private key (64 hex chars, 32 bytes), or
 * - BIP39 mnemonic (12 or 24 words, English). Derives key via BIP32 path m/44'/0'/0'/0/0.
 *   Passphrase is not supported.
 */
export function importIdentity(
  seedOrPrivateKey: string,
  name?: string,
): LocalIdentity | null {
  const trimmed = seedOrPrivateKey.trim();
  const rawHex = trimmed.toLowerCase().replace(/^0x/, '');

  let privateKeyHex: string;
  try {
    if (/^[0-9a-f]{64}$/.test(rawHex)) {
      privateKeyHex = rawHex;
    } else {
      const normalized = trimmed.replace(/\s+/g, ' ').trim().toLowerCase();
      const words = normalized.split(' ');
      if (
        (words.length !== 12 && words.length !== 24) ||
        !validateMnemonic(normalized, englishWordlist)
      ) {
        console.error(
          'Import identity: expected 64 hex chars or valid BIP39 mnemonic (12/24 English words).',
        );
        return null;
      }
      privateKeyHex = privateKeyFromMnemonic(normalized);
    }
  } catch (error) {
    console.error('Error importing identity:', error);
    return null;
  }

  try {
    const publicKey = publicKeyFromPrivateKey(privateKeyHex);
    const identity: LocalIdentity = {
      uuid: generateUuid(),
      privateKey: privateKeyHex,
      publicKey,
      name,
      t0_anniversaire: Date.now(),
      version: '1.0',
    };
    storeIdentity(identity);
    return identity;
  } catch (error) {
    console.error('Error importing identity:', error);
    return null;
  }
}

export function isProtectionEnabled(): boolean {
  return localStorage.getItem(STORAGE_KEY_IDENTITY_ENCRYPTED) !== null;
}

/**
 * Enable password protection: encrypt private key, store encrypted blob, remove from identity.
 */
export async function enableProtection(password: string): Promise<void> {
  const identity = getStoredIdentity();
  if (identity?.privateKey === undefined) {
    throw new Error('No identity or private key to protect');
  }
  const payload = await encryptPrivateKey(identity.privateKey, password);
  localStorage.setItem(
    STORAGE_KEY_IDENTITY_ENCRYPTED,
    JSON.stringify(payload),
  );
  const { privateKey: _pk, ...meta } = identity;
  storeIdentity(meta as LocalIdentity);
  setUnlockedPrivateKey(null);
}

/**
 * Unlock: decrypt and store private key in session.
 */
export async function unlock(password: string): Promise<void> {
  const raw = localStorage.getItem(STORAGE_KEY_IDENTITY_ENCRYPTED);
  if (raw === null) {
    throw new Error('Protection not enabled');
  }
  const payload = JSON.parse(raw) as EncryptedPayload;
  const pk = await decryptPrivateKey(payload, password);
  setUnlockedPrivateKey(pk);
}

/**
 * Lock: clear session (encrypted storage unchanged).
 */
export function lock(): void {
  setUnlockedPrivateKey(null);
}

/**
 * Disable protection: decrypt, store private key in identity, remove encrypted blob.
 */
export async function disableProtection(password: string): Promise<void> {
  const raw = localStorage.getItem(STORAGE_KEY_IDENTITY_ENCRYPTED);
  if (raw === null) {
    throw new Error('Protection not enabled');
  }
  const payload = JSON.parse(raw) as EncryptedPayload;
  const pk = await decryptPrivateKey(payload, password);
  const identity = getStoredIdentity();
  if (identity === null) {
    throw new Error('No stored identity');
  }
  storeIdentity({ ...identity, privateKey: pk });
  localStorage.removeItem(STORAGE_KEY_IDENTITY_ENCRYPTED);
  setUnlockedPrivateKey(null);
}

/**
 * Clear local identity and protected blob. Session key cleared. Pairs unchanged;
 * caller should clear pairs separately if desired.
 */
export function clearLocalIdentity(): void {
  localStorage.removeItem(STORAGE_KEY_IDENTITY);
  localStorage.removeItem(STORAGE_KEY_IDENTITY_ENCRYPTED);
  setUnlockedPrivateKey(null);
}
